import {
  ApFlagId,
  FlowRun,
  flowStructureUtil,
  FlowRunStatus,
  FlowVersion,
  isNil,
  Permission,
  RunEnvironment,
} from '@activepieces/shared';
import { t } from 'i18next';
import { ChevronLeft, Info, Sparkles } from 'lucide-react';
import React, { useMemo } from 'react';

import { flowRunUtils } from '../../../features/flow-runs/lib/flow-run-utils';
import {
  PendingAiBuilderAbout,
  useChatStore,
} from '../ai-builder/useChatStore';
import { SidebarHeader } from '../sidebar-header';

import { FlowStepDetailsCardItem } from './flow-step-details-card-item';
import { FlowStepInputOutput } from './flow-step-input-output';

import {
  LeftSideBarType,
  useBuilderStateContext,
  useSwitchToDraft,
} from '@/app/builder/builder-hooks';
import { Button } from '@/components/ui/button';
import { CardList } from '@/components/ui/card-list';
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from '@/components/ui/resizable-panel';
import { LoadingSpinner } from '@/components/ui/spinner';
import { useAuthorization } from '@/hooks/authorization-hooks';
import { flagsHooks } from '@/hooks/flags-hooks';
import { formatUtils } from '@/lib/utils';

/** The run's own failed step, labelled for the "Understand this error" pointer */
const failedRunAbout = (
  run: FlowRun,
  flowVersion: FlowVersion,
): PendingAiBuilderAbout => {
  const stepName = run.steps
    ? flowRunUtils.findFailedStepInOutput(run.steps)
    : null;
  const stepDisplayName = stepName
    ? flowStructureUtil.getStep(stepName, flowVersion)?.displayName
    : undefined;
  const date = formatUtils.formatDate(new Date(run.startTime));
  const label = stepDisplayName
    ? t('About: run of {date}, step {step}', { date, step: stepDisplayName })
    : t('About: run of {date}', { date });
  return {
    about: { type: 'run', runId: run.id, stepName: stepName ?? undefined },
    label,
    run,
  };
};

function getMessage(run: FlowRun | null, retentionDays: number | null) {
  if (!run || run.status === FlowRunStatus.RUNNING) return null;
  if ([FlowRunStatus.INTERNAL_ERROR].includes(run.status)) {
    return t('There are no logs captured for this run.');
  }
  if (run.status === FlowRunStatus.RATE_LIMITED) {
    return t(
      'The flow was put in the queue due to too many executions in too short time window (20/minute max). It will be executed in few minutes.',
    );
  }
  if (isNil(run.logsFileId)) {
    return t(
      'Logs are kept for {days} days after execution and then deleted.',
      { days: retentionDays },
    );
  }
  return null;
}
const FlowRunDetails = React.memo(() => {
  const { data: rententionDays } = flagsHooks.useFlag<number>(
    ApFlagId.EXECUTION_DATA_RETENTION_DAYS,
  );

  const [setLeftSidebar, run, steps, loopsIndexes, flowVersion, selectedStep] =
    useBuilderStateContext((state) => {
      const steps =
        state.run && state.run.steps ? Object.keys(state.run.steps) : [];
      return [
        state.setLeftSidebar,
        state.run,
        steps,
        state.loopsIndexes,
        state.flowVersion,
        state.selectedStep,
      ];
    });
  const setPendingAbout = useChatStore((state) => state.setPendingAbout);
  const { checkAccess } = useAuthorization();
  const userHasPermissionToEditFlow = checkAccess(Permission.WRITE_FLOW);
  // Same "which run is a failure" answer the run list already renders with
  // (flowRunUtils.getStatusIcon) — one owner, not a second classification
  const isFailedRun =
    run !== null && flowRunUtils.getStatusIcon(run.status).variant === 'error';
  // Same way out as the "Exit Run" button: a raw `exitRun` leaves
  // `flowVersion` at the run's version, which the next AI turn would snapshot
  // as `previousFlowVersion` — "Undo" would then overwrite the real draft.
  // The switch ends with exitRun, which closes every sidebar: the chat opens
  // after it
  const { switchToDraft } = useSwitchToDraft({
    onSwitched: () => setLeftSidebar(LeftSideBarType.AI_BUILDER),
  });

  const askAboutFailedRun = () => {
    if (!run) return;
    setPendingAbout(flowVersion.flowId, failedRunAbout(run, flowVersion));
    switchToDraft();
  };

  const selectedStepOutput = useMemo(() => {
    return run && selectedStep && run.steps
      ? flowRunUtils.extractStepOutput(
          selectedStep,
          loopsIndexes,
          run.steps,
          flowVersion,
        )
      : null;
  }, [run, selectedStep, loopsIndexes, flowVersion]);

  const message = getMessage(run, rententionDays);

  if (!isNil(message))
    return (
      <div className="flex flex-col justify-center items-center gap-4 w-full h-full">
        <Info size={36} className="text-muted-foreground" />
        <h4 className="px-6 text-sm text-center text-muted-foreground ">
          {message}
        </h4>
      </div>
    );

  return (
    <ResizablePanelGroup direction="vertical">
      <SidebarHeader onClose={() => setLeftSidebar(LeftSideBarType.NONE)}>
        <div className="flex gap-2 items-center">
          {run && run.environment !== RunEnvironment.TESTING && (
            <Button
              variant="ghost"
              size={'sm'}
              onClick={() => setLeftSidebar(LeftSideBarType.RUNS)}
            >
              <ChevronLeft size={16} />
            </Button>
          )}
          <span>{t('Run Details')}</span>
        </div>
      </SidebarHeader>
      {isFailedRun && userHasPermissionToEditFlow && (
        <div className="flex justify-end px-2 pb-2">
          <Button variant="outline" size="sm" onClick={askAboutFailedRun}>
            <Sparkles size={14} className="mr-1" />
            {t('Understand this error')}
          </Button>
        </div>
      )}
      <ResizablePanel className="h-full">
        <CardList className="p-0 h-full" listClassName="gap-0.5">
          {steps.length > 0 &&
            steps
              .filter((path) => !isNil(path))
              .map((path) => (
                <FlowStepDetailsCardItem
                  stepName={path}
                  depth={0}
                  key={path}
                ></FlowStepDetailsCardItem>
              ))}
          {steps.length === 0 && (
            <div className="w-full h-full flex items-center justify-center">
              <LoadingSpinner></LoadingSpinner>
            </div>
          )}
        </CardList>
      </ResizablePanel>
      {selectedStepOutput && (
        <>
          <ResizableHandle withHandle={true} />
          <ResizablePanel defaultValue={25}>
            <FlowStepInputOutput
              stepDetails={selectedStepOutput}
            ></FlowStepInputOutput>
          </ResizablePanel>
        </>
      )}
    </ResizablePanelGroup>
  );
});

FlowRunDetails.displayName = 'FlowRunDetails';
export { FlowRunDetails };
